import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Package, Plus, X, Edit, Search, TrendingUp, TrendingDown, AlertTriangle, Clock, Barcode, Printer, RefreshCw, Upload, FileSpreadsheet, CheckCircle, Download } from 'lucide-react';

const BASE = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/inventario`;

export default function ProductosManager() {
    const [productos, setProductos] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isHistorialOpen, setIsHistorialOpen] = useState(false);
    const [selectedProducto, setSelectedProducto] = useState(null);
    const [historial, setHistorial] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [formData, setFormData] = useState({
        id: null, codigo_barras: '', nombre: '', descripcion: '',
        marca: '', color: '', talle: '',
        precio_venta: '', costo_compra: '', categoria: '',
        stock_actual: 0, stock_minimo: 5, tasa_iva: 10, activo: true, imagen_url: ''
    });
    const [filtroCategoria, setFiltroCategoria] = useState('');
    const [filtroMarca, setFiltroMarca] = useState('');
    const [filtroStock, setFiltroStock] = useState('');
    const [filtroActivo, setFiltroActivo] = useState('activo');
    const [isImportOpen, setIsImportOpen] = useState(false);
    const [importPhase, setImportPhase] = useState('select'); // 'select' | 'preview' | 'done'
    const [importFile, setImportFile] = useState(null);
    const [importPreview, setImportPreview] = useState([]);
    const [importErrors, setImportErrors] = useState([]);
    const [importLoading, setImportLoading] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [importDragOver, setImportDragOver] = useState(false);
    const fileInputRef = useRef(null);


    const fetchData = async () => {
        try {
            const [resProd, resCat] = await Promise.all([
                axios.get(`${BASE}/productos/`),
                axios.get(`${BASE}/categorias/`)
            ]);
            setProductos(resProd.data);
            setCategorias(resCat.data);
        } catch { setError('Error al cargar productos.'); }
    };

    useEffect(() => { fetchData(); }, []);

    const handleOpenModal = (prod = null) => {
        if (prod) {
            setFormData({
                id: prod.id, codigo_barras: prod.codigo_barras, nombre: prod.nombre,
                marca: prod.marca || '', color: prod.color || '', talle: prod.talle || '',
                descripcion: prod.descripcion || '', precio_venta: prod.precio_venta,
                costo_compra: prod.costo_compra, categoria: prod.categoria || '',
                stock_actual: prod.stock_actual, stock_minimo: prod.stock_minimo,
                tasa_iva: prod.tasa_iva ?? 10,
                activo: prod.activo, imagen_url: prod.imagen_url || ''
            });
        } else {
            setFormData({ id: null, tasa_iva: 10, codigo_barras: '', nombre: '', marca: '', color: '', talle: '', descripcion: '', precio_venta: '', costo_compra: '', categoria: '', stock_actual: 0, stock_minimo: 5, activo: true, imagen_url: '' });
        }
        setError(null);
        setIsModalOpen(true);
    };

    const handleOpenHistorial = async (prod) => {
        setSelectedProducto(prod);
        try {
            const res = await axios.get(`${BASE}/productos/${prod.id}/historial/`);
            setHistorial(res.data);
        } catch { setHistorial([]); }
        setIsHistorialOpen(true);
    };

    const generateInternalBarcode = () => {
        // Generamos un código único basado en timestamp para evitar duplicados internos
        const code = "INT" + Date.now().toString().slice(-9);
        setFormData({ ...formData, codigo_barras: code });
    };

    const printBarcode = (producto) => {
        const printWindow = window.open('', '_blank', 'width=600,height=600');
        printWindow.document.write(`
            <html>
                <head>
                    <title>Etiqueta - ${producto.nombre}</title>
                    <style>
                        @page { size: auto; margin: 0mm; }
                        body {
                            display: flex; flex-direction: column; align-items: center; justify-content: center;
                            height: 100vh; margin: 0; font-family: 'Inter', sans-serif; background: white;
                        }
                        .label-container {
                            border: 2px dashed #ccc; padding: 20px; text-align: center; width: 300px;
                        }
                        .product-name { font-weight: 800; font-size: 16px; margin-bottom: 10px; color: #1a1a1a; }
                        .price-tag { font-size: 24px; font-weight: 900; margin-top: 10px; color: #059669; }
                    </style>
                </head>
                <body>
                    <div class="label-container">
                        <div class="product-name">${producto.nombre}</div>
                        <svg id="barcode"></svg>
                        <div class="price-tag">Gs. ${parseFloat(producto.precio_venta).toLocaleString()}</div>
                    </div>
                    <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.0/dist/JsBarcode.all.min.js"></script>
                    <script>
                        JsBarcode("#barcode", "${producto.codigo_barras}", {
                            format: "CODE128",
                            width: 2,
                            height: 60,
                            displayValue: true,
                            fontSize: 14,
                            fontOptions: "bold"
                        });
                        setTimeout(() => {
                            window.print();
                            window.close();
                        }, 800);
                    </script>
                </body>
            </html>
        `);
        printWindow.document.close();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const data = { ...formData, categoria: formData.categoria || null };
            if (formData.id) {
                await axios.put(`${BASE}/productos/${formData.id}/`, data);
            } else {
                await axios.post(`${BASE}/productos/`, data);
            }
            setIsModalOpen(false);
            fetchData();
        } catch (err) {
            console.error(err);
            if (err.response?.status === 403) {
                setError(err.response.data.message || 'Límite de artículos alcanzado. Contacte con Administración.');
            } else {
                const d = err.response?.data;
                setError(d?.codigo_barras?.[0] || d?.nombre?.[0] || 'Error al guardar el producto.');
            }
        } finally { setLoading(false); }
    };

    const marcasUnicas = [...new Set(productos.map(p => p.marca).filter(Boolean))].sort();

    const productosFiltrados = productos.filter(p => {
        if (search && !p.nombre.toLowerCase().includes(search.toLowerCase()) && !p.codigo_barras.includes(search)) return false;
        if (filtroCategoria && String(p.categoria) !== filtroCategoria) return false;
        if (filtroMarca && p.marca !== filtroMarca) return false;
        if (filtroStock === 'bajo' && p.stock_actual > p.stock_minimo) return false;
        if (filtroActivo === 'activo' && !p.activo) return false;
        if (filtroActivo === 'inactivo' && p.activo) return false;
        return true;
    });

    const hayFiltros = filtroCategoria || filtroMarca || filtroStock || filtroActivo !== 'activo';
    const limpiarFiltros = () => { setFiltroCategoria(''); setFiltroMarca(''); setFiltroStock(''); setFiltroActivo('activo'); };

    const calcMargen = (venta, costo) => {
        if (!costo || costo <= 0) return 0;
        return (((venta - costo) / costo) * 100).toFixed(1);
    };

    const openImport = () => {
        setImportPhase('select');
        setImportFile(null);
        setImportPreview([]);
        setImportErrors([]);
        setImportResult(null);
        setIsImportOpen(true);
    };

    const handleImportFile = async (file) => {
        if (!file) return;
        setImportFile(file);
        setImportLoading(true);
        setImportErrors([]);
        try {
            const fd = new FormData();
            fd.append('archivo', file);
            fd.append('confirmar', 'false');
            const res = await axios.post(`${BASE}/productos/importar/`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setImportPreview(res.data.filas);
            setImportPhase('preview');
        } catch (err) {
            const data = err.response?.data;
            if (data?.errores) {
                setImportErrors(data.errores);
                setImportPhase('preview');
            } else {
                setImportErrors([{ fila: '-', error: data?.error || 'Error al procesar el archivo.' }]);
                setImportPhase('preview');
            }
        } finally { setImportLoading(false); }
    };

    const handleImportConfirm = async () => {
        if (!importFile) return;
        setImportLoading(true);
        try {
            const fd = new FormData();
            fd.append('archivo', importFile);
            fd.append('confirmar', 'true');
            const res = await axios.post(`${BASE}/productos/importar/`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setImportResult(res.data);
            setImportPhase('done');
            fetchData();
        } catch (err) {
            const data = err.response?.data;
            setImportErrors([{ fila: '-', error: data?.error || 'Error al importar.' }]);
        } finally { setImportLoading(false); }
    };

    const downloadTemplate = () => {
        // La plantilla incluye la tasa de IVA, con un ejemplo de cada caso.
        // Sin una exenta a la vista nadie se entera de que la columna existe,
        // y todo el catálogo entra al 10%.
        const csv = 'nombre,codigo,categoria,precio_venta,precio_costo,stock,stock_minimo,tasa_iva,marca,descripcion\n' +
            'Tornillo 1/4",TRN001,Tornillos,1500,800,100,10,10,FastFix,Tornillo autoperforante\n' +
            'Pintura Blanca 1L,PNT001,Pinturas,35000,20000,20,5,10,Sherwin,Pintura látex\n' +
            'Manual de instalación,LIB001,Libros,25000,15000,5,1,0,,Artículo exento de IVA\n';
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = 'plantilla_productos.csv'; a.click();
        URL.revokeObjectURL(url);
    };

    const fmt = (n) => parseFloat(n || 0).toLocaleString('es-PY');

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between gap-4 items-start md:items-center bg-white dark:bg-slate-900/60 backdrop-blur-sm p-6 rounded-2xl border border-slate-200 dark:border-white/10">
                <div className="flex items-center gap-4">
                    <div className="bg-brand-500/20 p-3 rounded-xl">
                        <Package size={24} className="text-brand-800" />
                    </div>
                    <div>
                        <h2 className="titulo-modal">Catálogo de Productos</h2>
                        <p className="text-sm text-slate-500 dark:text-gray-400">{productos.length} artículos registrados</p>
                    </div>
                </div>
                <div className="flex gap-3 w-full md:w-auto">
                    <div className="relative flex-1 md:w-64">
                        <Search size={16} className="icono-campo" />
                        <input
                            type="text" placeholder="Buscar por nombre o código..."
                            value={search} onChange={e => setSearch(e.target.value)}
                            className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white text-sm placeholder-slate-400 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500"
                        />
                    </div>
                    <button
                        onClick={openImport}
                        className="bg-brand-500 hover:bg-brand-400 text-negro-950 px-4 py-2.5 rounded-xl font-semibold flex items-center gap-2 transition-all whitespace-nowrap shadow-lg shadow-exito-500/30"
                    >
                        <Upload size={18} /> <span className="hidden sm:inline">Importar</span>
                    </button>
                    <button
                        onClick={() => handleOpenModal()}
                        className="bg-brand-500 hover:bg-brand-400 text-negro-950 px-5 py-2.5 rounded-xl font-semibold flex items-center gap-2 transition-all whitespace-nowrap shadow-lg shadow-brand-500/30"
                    >
                        <Plus size={18} /> Nuevo
                    </button>
                </div>
            </div>

            {error && !isModalOpen && (
                <div className="bg-error-500/10 border border-error-500/30 text-error-400 p-4 rounded-xl">{error}</div>
            )}

            {/* Filtros */}
            <div className="flex flex-wrap gap-2 items-center">
                <select
                    value={filtroCategoria}
                    onChange={e => setFiltroCategoria(e.target.value)}
                    className="px-3 py-2 rounded-xl text-sm bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                    <option value="">Todas las categorías</option>
                    {categorias.map(c => <option key={c.id} value={String(c.id)}>{c.nombre}</option>)}
                </select>
                {marcasUnicas.length > 0 && (
                    <select
                        value={filtroMarca}
                        onChange={e => setFiltroMarca(e.target.value)}
                        className="px-3 py-2 rounded-xl text-sm bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-brand-500"
                    >
                        <option value="">Todas las marcas</option>
                        {marcasUnicas.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                )}
                <button
                    onClick={() => setFiltroStock(filtroStock === 'bajo' ? '' : 'bajo')}
                    className={`px-3 py-2 rounded-xl text-sm font-medium flex items-center gap-1.5 border transition-colors ${
                        filtroStock === 'bajo'
                            ? 'bg-alerta-500/20 border-alerta-500/40 text-alerta-600 dark:text-alerta-400'
                            : 'bg-white dark:bg-white/5 border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-400 hover:border-alerta-400/50'
                    }`}
                >
                    <AlertTriangle size={14} /> Stock bajo
                </button>
                <select
                    value={filtroActivo}
                    onChange={e => setFiltroActivo(e.target.value)}
                    className="px-3 py-2 rounded-xl text-sm bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                    <option value="">Todos (activos + inactivos)</option>
                    <option value="activo">Solo activos</option>
                    <option value="inactivo">Solo inactivos</option>
                </select>
                {hayFiltros && (
                    <button onClick={limpiarFiltros} className="px-3 py-2 rounded-xl text-xs text-slate-500 dark:text-gray-400 hover:text-slate-700 dark:hover:text-white underline transition-colors">
                        Limpiar filtros
                    </button>
                )}
                <span className="ml-auto text-xs text-slate-400 dark:text-gray-500">
                    {productosFiltrados.length} de {productos.length}
                </span>
            </div>

            {/* Tabla */}
            <div className="bg-white dark:bg-slate-900/60 backdrop-blur-sm border border-slate-200 dark:border-white/10 rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="tabla-cabecera">
                                <th className="px-6 py-4 font-medium">Producto</th>
                                <th className="px-6 py-4 font-medium">Código</th>
                                <th className="px-6 py-4 font-medium">Categoría</th>
                                <th className="px-6 py-4 font-medium text-right">Costo</th>
                                <th className="px-6 py-4 font-medium text-right">Venta</th>
                                <th className="px-6 py-4 font-medium text-center">Margen</th>
                                <th className="px-6 py-4 font-medium text-center">En stock</th>
                                <th className="px-6 py-4 font-medium text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="tabla-filas">
                            {productosFiltrados.map(p => {
                                const margen = calcMargen(parseFloat(p.precio_venta), parseFloat(p.costo_compra));
                                const stockBajo = p.stock_actual <= p.stock_minimo;
                                return (
                                    <tr key={p.id} className="hover:bg-slate-50 dark:hover:bg-white/5 transition-colors group">
                                        <td className="px-6 py-4">
                                            <div className="font-semibold text-slate-900 dark:text-white">{p.nombre}</div>
                                            <div className="flex gap-2 mt-1">
                                                {p.marca && <span className="text-[10px] bg-brand-500/10 text-brand-800 px-1.5 py-0.5 rounded uppercase font-black">{p.marca}</span>}
                                                {p.talle && <span className="text-[10px] bg-slate-500/20 text-slate-400 px-1.5 py-0.5 rounded uppercase font-black">{p.talle}</span>}
                                            </div>
                                            {!p.activo && <span className="text-xs text-error-400 block mt-1">• Inactivo</span>}
                                        </td>
                                        <td className="px-6 py-4 text-slate-400 dark:text-gray-500 font-mono text-xs">{p.codigo_barras}</td>
                                        <td className="px-6 py-4">
                                            {p.categoria_nombre
                                                ? <span className="bg-slate-100 dark:bg-white/10 text-slate-600 dark:text-gray-300 px-2 py-1 rounded-lg text-xs">{p.categoria_nombre}</span>
                                                : <span className="text-slate-400 dark:text-gray-600 text-xs">—</span>}
                                        </td>
                                        <td className="px-6 py-4 text-right text-slate-600 dark:text-gray-300 font-mono">Gs. {parseFloat(p.costo_compra).toLocaleString()}</td>
                                        <td className="px-6 py-4 text-right">
                                            <button
                                                type="button"
                                                onClick={() => handleOpenModal(p)}
                                                title="Cambiar el precio"
                                                className="font-bold font-mono text-slate-900 dark:text-white hover:text-brand-800 dark:hover:text-brand-500 hover:underline underline-offset-4 transition-colors"
                                            >
                                                Gs. {parseFloat(p.precio_venta).toLocaleString()}
                                            </button>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <span className={`text-xs font-bold px-2 py-1 rounded-full inline-flex items-center gap-1 ${parseFloat(margen) >= 20 ? 'bg-exito-500/20 text-exito-400' : parseFloat(margen) >= 0 ? 'bg-alerta-500/20 text-alerta-400' : 'bg-error-500/20 text-error-400'}`}>
                                                {parseFloat(margen) >= 0 ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                                                {margen}%
                                            </span>
                                        </td>
                                        {/* El stock disponible y el mínimo, en lineas separadas.
                                            Antes se mostraban como «4 / 5», que se lee «4 de 5»:
                                            el segundo número parecía un total cuando en realidad
                                            es el umbral de reposición. */}
                                        <td className="px-6 py-4 text-center">
                                            <div className="flex items-center justify-center gap-1.5">
                                                {stockBajo && <AlertTriangle size={14} className="text-alerta-400" />}
                                                <span className={`font-bold ${stockBajo ? 'text-alerta-400' : 'text-slate-900 dark:text-white'}`}>
                                                    {p.stock_actual}
                                                </span>
                                                <span className="text-slate-400 dark:text-gray-600 text-xs">un.</span>
                                            </div>
                                            <div className="text-[10px] text-slate-400 dark:text-gray-600 mt-0.5">
                                                mínimo {p.stock_minimo}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => printBarcode(p)}
                                                    title="Imprimir Etiqueta"
                                                    className="accion-fila"
                                                >
                                                    <Printer size={14} />
                                                </button>
                                                <button
                                                    onClick={() => handleOpenHistorial(p)}
                                                    title="Ver historial de precios"
                                                    className="accion-fila"
                                                >
                                                    <Clock size={14} />
                                                </button>
                                                {/* Editar es la acción principal de la fila —es
                                                    donde se cambian los precios— y tiene que leerse
                                                    como tal. Con tres íconos grises de 14 px
                                                    iguales, el único que decía cuál era cuál era el
                                                    texto que aparece al pasar el mouse. */}
                                                <button
                                                    onClick={() => handleOpenModal(p)}
                                                    title="Editar artículo: precios, stock mínimo, IVA"
                                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-500 hover:bg-brand-400 text-negro-950 text-xs font-bold transition-colors"
                                                >
                                                    <Edit size={14} />
                                                    Editar
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            {productosFiltrados.length === 0 && (
                                <tr><td colSpan="8" className="px-6 py-12 text-center text-slate-400 dark:text-gray-600">
                                    <Package size={40} className="mx-auto mb-3 opacity-30" />
                                    {search ? 'No se encontraron productos.' : 'No hay productos registrados.'}
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal Editar/Crear */}
            {isModalOpen && (
                <div className="fondo-modal">
                    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/20 rounded-3xl w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center p-6 border-b border-slate-200 dark:border-white/10 sticky top-0 bg-white dark:bg-slate-900 z-10">
                            <h3 className="titulo-modal">
                                <Package className="text-brand-800" size={20} />
                                {formData.id ? 'Editar Producto' : 'Nuevo Producto'}
                            </h3>
                            <button onClick={() => setIsModalOpen(false)} className="boton-icono"><X size={20} /></button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-6 space-y-5">
                            {error && <div className="bg-error-500/10 border border-error-500/30 text-error-400 p-3 rounded-xl text-sm">{error}</div>}
                                           <div className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label htmlFor="producto-nombre-del-producto" className="etiqueta-campo normal-case text-sm">Nombre del Producto *</label>
                                        <input id="producto-nombre-del-producto" required type="text" className="campo" placeholder="Ej: Martillo 500g"
                                            value={formData.nombre} onChange={e => setFormData({ ...formData, nombre: e.target.value })} />
                                    </div>
                                    <div>
                                        <label htmlFor="producto-marca" className="etiqueta-campo normal-case text-sm">Marca</label>
                                        <input id="producto-marca" type="text" className="campo" placeholder="Stanley, Tramontina, etc."
                                            value={formData.marca} onChange={e => {
                                                const val = e.target.value;
                                                const cursor = e.target.selectionStart;
                                                setFormData({ ...formData, marca: val.replace(/\b\w/g, c => c.toUpperCase()) });
                                                requestAnimationFrame(() => { e.target.setSelectionRange(cursor, cursor); });
                                            }} />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                                    <div className="col-span-2">
                                        <label htmlFor="producto-codigo-barras" className="block text-sm font-semibold text-slate-600 dark:text-gray-300 mb-1.5 flex justify-between">
                                            Código de Barras *
                                            {!formData.id && (
                                                <button
                                                    type="button"
                                                    onClick={generateInternalBarcode}
                                                    className="text-[10px] text-brand-800 hover:text-brand-800 flex items-center gap-1 uppercase tracking-wider font-black"
                                                >
                                                    <RefreshCw size={10} /> Generar
                                                </button>
                                            )}
                                            {formData.id && (
                                                <span className="text-[10px] text-slate-400 dark:text-slate-500 uppercase tracking-wider font-semibold">Bloqueado</span>
                                            )}
                                        </label>
                                        <div className="relative">
                                            <Barcode size={16} className="icono-campo" />
                                            <input id="producto-codigo-barras"
                                                required
                                                type="text"
                                                readOnly={!!formData.id}
                                                className={`w-full pl-10 pr-4 py-2.5 rounded-xl font-mono text-sm focus:outline-none ${
                                                    formData.id
                                                        ? 'bg-slate-200 dark:bg-white/5 border border-slate-300 dark:border-white/5 text-slate-500 dark:text-slate-500 cursor-not-allowed select-none'
                                                        : 'bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500'
                                                }`}
                                                placeholder="789... o Interno"
                                                value={formData.codigo_barras}
                                                onChange={e => { if (!formData.id) setFormData({ ...formData, codigo_barras: e.target.value }); }}
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label htmlFor="producto-color" className="etiqueta-campo normal-case text-sm">Color</label>
                                        <input id="producto-color" type="text" className="w-full px-4 py-2.5 rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" placeholder="Rojo, Gris..."
                                            value={formData.color} onChange={e => setFormData({ ...formData, color: e.target.value })} />
                                    </div>
                                    <div>
                                        <label htmlFor="producto-talle-medida" className="etiqueta-campo normal-case text-sm">Talle/Medida</label>
                                        <input id="producto-talle-medida" type="text" className="w-full px-4 py-2.5 rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" placeholder="L, 12mm, 1/2'..."
                                            value={formData.talle} onChange={e => setFormData({ ...formData, talle: e.target.value })} />
                                    </div>
                                </div>
                                <div>
                                    <label htmlFor="producto-categoria" className="etiqueta-campo normal-case text-sm">Categoría</label>
                                    <select id="producto-categoria" className="w-full px-4 py-2.5 rounded-xl bg-white dark:bg-slate-800 border border-slate-300 dark:border-white/10 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500"
                                        value={formData.categoria} onChange={e => setFormData({ ...formData, categoria: e.target.value })}>
                                        <option value="">Sin categoría</option>
                                        {categorias.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-slate-100 dark:bg-white/5 rounded-xl border border-slate-200 dark:border-white/10">
                                <div>
                                    <label htmlFor="producto-precio-costo" className="etiqueta-campo normal-case text-sm">💰 Precio Costo *</label>
                                    <input id="producto-precio-costo" required type="number" step="1" min="0" className="w-full px-4 py-2.5 rounded-xl bg-white dark:bg-slate-800 border border-slate-300 dark:border-white/10 text-slate-900 dark:text-white font-mono focus:outline-none focus:ring-2 focus:ring-alerta-500" placeholder="0"
                                        value={formData.costo_compra} onChange={e => setFormData({ ...formData, costo_compra: e.target.value })} />
                                    <p className="text-xs text-slate-400 dark:text-gray-600 mt-1">Precio al que compras</p>
                                </div>
                                <div>
                                    <label htmlFor="producto-precio-venta" className="etiqueta-campo normal-case text-sm">🏷️ Precio Venta *</label>
                                    <input id="producto-precio-venta" required type="number" step="1" min="0" className="w-full px-4 py-2.5 rounded-xl bg-white dark:bg-slate-800 border border-slate-300 dark:border-white/10 text-slate-900 dark:text-white font-mono focus:outline-none focus:ring-2 focus:ring-brand-500" placeholder="0"
                                        value={formData.precio_venta} onChange={e => setFormData({ ...formData, precio_venta: e.target.value })} />
                                    <p className="text-xs text-slate-400 dark:text-gray-600 mt-1">
                                        Margen: {formData.costo_compra > 0 ? (((formData.precio_venta - formData.costo_compra) / formData.costo_compra) * 100).toFixed(1) : 0}%
                                    </p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="producto-stock-actual" className="block text-sm font-semibold text-slate-600 dark:text-gray-300 mb-1.5 flex items-center justify-between">
                                        Stock Actual
                                        {formData.id && <span className="text-[10px] text-slate-400 dark:text-slate-500 font-semibold uppercase tracking-wider">Bloqueado · usar Ajuste</span>}
                                    </label>
                                    <input id="producto-stock-actual"
                                        type="number" min="0"
                                        readOnly={!!formData.id}
                                        className={`w-full px-4 py-2.5 rounded-xl text-sm focus:outline-none ${
                                            formData.id
                                                ? 'bg-slate-200 dark:bg-white/5 border border-slate-300 dark:border-white/5 text-slate-500 dark:text-slate-500 cursor-not-allowed'
                                                : 'bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500'
                                        }`}
                                        value={formData.stock_actual}
                                        onChange={e => { if (!formData.id) setFormData({ ...formData, stock_actual: e.target.value }); }}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="producto-iva" className="etiqueta-campo normal-case text-sm">IVA</label>
                                    <select id="producto-iva" className="campo"
                                        value={formData.tasa_iva}
                                        onChange={e => setFormData({ ...formData, tasa_iva: Number(e.target.value) })}>
                                        <option value={10}>IVA 10%</option>
                                        <option value={5}>IVA 5%</option>
                                        <option value={0}>Exenta</option>
                                    </select>
                                    <p className="ayuda-campo">La mayoría de los artículos de ferretería tributan 10%.</p>
                                </div>
                                <div>
                                    <label htmlFor="producto-stock-minimo" className="etiqueta-campo normal-case text-sm">Stock Mínimo</label>
                                    <input id="producto-stock-minimo" type="number" min="0" className="campo"
                                        value={formData.stock_minimo} onChange={e => setFormData({ ...formData, stock_minimo: e.target.value })} />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="producto-descripcion" className="etiqueta-campo normal-case text-sm">Descripción</label>
                                <textarea id="producto-descripcion" rows={2} className="w-full px-4 py-2.5 rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white resize-none focus:outline-none focus:ring-2 focus:ring-brand-500" placeholder="Descripción del producto..."
                                    value={formData.descripcion} onChange={e => setFormData({ ...formData, descripcion: e.target.value })} />
                            </div>

                            <div className="flex items-center gap-3 p-3 bg-slate-100 dark:bg-white/5 rounded-xl">
                                <label htmlFor="producto-producto-activo" className="text-sm font-semibold text-slate-600 dark:text-gray-300 flex-1">Producto Activo</label>
                                <button type="button" onClick={() => setFormData({ ...formData, activo: !formData.activo })}
                                    className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${formData.activo ? 'bg-brand-500' : 'bg-gray-600'}`}>
                                    <span className={`inline-block w-5 h-5 bg-white rounded-full transition-transform ${formData.activo ? 'translate-x-6' : 'translate-x-1'}`} />
                                </button>
                            </div>

                            <div className="pie-modal">
                                <button type="button" onClick={() => setIsModalOpen(false)} className="px-5 py-2.5 rounded-xl text-slate-500 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/10 transition-colors">Cancelar</button>
                                <button type="submit" disabled={loading} className="bg-brand-500 hover:bg-brand-400 text-negro-950 px-6 py-2.5 rounded-xl font-semibold disabled:opacity-50 transition-all">
                                    {loading ? 'Guardando...' : (formData.id ? 'Guardar Cambios' : 'Crear Producto')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal Historial de Precios */}
            {isHistorialOpen && selectedProducto && (
                <div className="fondo-modal">
                    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/20 rounded-3xl w-full max-w-lg shadow-2xl max-h-[80vh] overflow-y-auto">
                        <div className="flex justify-between items-center p-6 border-b border-slate-200 dark:border-white/10 sticky top-0 bg-white dark:bg-slate-900 z-10">
                            <div>
                                <h3 className="titulo-modal">
                                    <Clock className="text-brand-800" size={20} /> Historial de Precios
                                </h3>
                                <p className="text-sm text-slate-500 dark:text-gray-400 mt-0.5">{selectedProducto.nombre}</p>
                            </div>
                            <button onClick={() => setIsHistorialOpen(false)} className="boton-icono"><X size={20} /></button>
                        </div>
                        <div className="p-6">
                            {historial.length === 0 ? (
                                <p className="text-center text-slate-400 dark:text-gray-600 py-8">Sin cambios de precio registrados.</p>
                            ) : (
                                <div className="space-y-3">
                                    {historial.map(h => (
                                        <div key={h.id} className="flex items-center justify-between p-4 bg-slate-50 dark:bg-white/5 rounded-xl border border-slate-200 dark:border-white/10">
                                            <div>
                                                <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${h.tipo === 'venta' ? 'bg-brand-500/20 text-brand-800' : 'bg-alerta-500/20 text-alerta-400'}`}>
                                                    {h.tipo === 'venta' ? 'Precio Venta' : 'Precio Costo'}
                                                </span>
                                                <p className="text-xs text-slate-400 dark:text-gray-500 mt-1">{new Date(h.fecha).toLocaleString()} · {h.modificado_por_nombre || 'Sistema'}</p>
                                            </div>
                                            <div className="flex items-center gap-2 text-sm font-mono">
                                                <span className="text-error-400 line-through">Gs. {parseFloat(h.precio_anterior).toLocaleString()}</span>
                                                <span className="text-slate-400 dark:text-gray-500">→</span>
                                                <span className="text-exito-400 font-bold">Gs. {parseFloat(h.precio_nuevo).toLocaleString()}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Importación */}
            {isImportOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-3xl w-full max-w-2xl shadow-2xl max-h-[90vh] flex flex-col">
                        {/* Header */}
                        <div className="flex justify-between items-center p-6 border-b border-slate-200 dark:border-white/10">
                            <h3 className="titulo-modal">
                                <FileSpreadsheet className="text-brand-800 dark:text-brand-400" size={20} />
                                Importar Productos
                            </h3>
                            <button onClick={() => setIsImportOpen(false)} className="boton-icono"><X size={20} /></button>
                        </div>

                        <div className="overflow-y-auto flex-1 p-6 space-y-4">
                            {/* Phase: select */}
                            {importPhase === 'select' && (
                                <>
                                    {/* Drop zone */}
                                    <div
                                        className={`border-2 border-dashed rounded-2xl p-10 text-center transition-colors cursor-pointer ${importDragOver ? 'border-exito-400 bg-exito-500/5' : 'border-slate-300 dark:border-white/20 hover:border-exito-400/60'}`}
                                        onClick={() => fileInputRef.current?.click()}
                                        onDragOver={e => { e.preventDefault(); setImportDragOver(true); }}
                                        onDragLeave={() => setImportDragOver(false)}
                                        onDrop={e => {
                                            e.preventDefault();
                                            setImportDragOver(false);
                                            const f = e.dataTransfer.files[0];
                                            if (f) handleImportFile(f);
                                        }}
                                    >
                                        <Upload size={36} className="mx-auto mb-3 text-slate-400 dark:text-gray-500" />
                                        <p className="font-semibold text-slate-700 dark:text-white">Arrastrá un archivo o hacé click para seleccionar</p>
                                        <p className="text-sm text-slate-400 dark:text-gray-500 mt-1">Formatos: .csv, .xlsx — máx. 5 MB</p>
                                        <input id="producto-producto-activo"
                                            ref={fileInputRef}
                                            type="file"
                                            accept=".csv,.xlsx"
                                            className="hidden"
                                            onChange={e => { const f = e.target.files[0]; if (f) handleImportFile(f); }}
                                        />
                                    </div>
                                    {importLoading && (
                                        <p className="text-center text-sm text-slate-500 dark:text-gray-400 animate-pulse">Procesando archivo...</p>
                                    )}
                                    {/* Columns reference */}
                                    <div className="bg-slate-50 dark:bg-white/5 rounded-xl p-4 text-xs text-slate-500 dark:text-gray-400 space-y-1">
                                        <p className="font-bold text-slate-700 dark:text-white text-sm mb-2">Columnas del archivo</p>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                                            <span><span className="text-error-400">*</span> <b>nombre</b> — nombre del producto</span>
                                            <span><b>codigo</b> — código/barras (si existe, actualiza)</span>
                                            <span><span className="text-error-400">*</span> <b>precio_venta</b> — precio de venta</span>
                                            <span><b>precio_costo</b> — costo de compra</span>
                                            <span><b>stock</b> — stock inicial (default 0)</span>
                                            <span><b>stock_minimo</b> — stock mínimo (default 5)</span>
                                            <span><b>categoria</b> — nombre (se crea si no existe)</span>
                                            <span><b>marca</b> — marca</span>
                                            <span><b>tasa_iva</b> — 10, 5 o 0 (exenta). Default 10</span>
                                            <span><b>descripcion</b> — descripción del artículo</span>
                                        </div>
                                        <p className="text-[11px] text-slate-500 dark:text-gray-400 mt-2">
                                            Revisá la columna <b>tasa_iva</b>: sin ella todo entra al 10%, y un
                                            artículo exento o al 5% quedaría facturado con el impuesto
                                            equivocado sin que nada lo avise.
                                        </p>
                                    </div>
                                    <button
                                        onClick={downloadTemplate}
                                        className="flex items-center gap-2 text-sm text-brand-800 hover:text-brand-800 transition-colors"
                                    >
                                        <Download size={14} /> Descargar plantilla CSV de ejemplo
                                    </button>
                                </>
                            )}

                            {/* Phase: preview */}
                            {importPhase === 'preview' && (
                                <>
                                    {importErrors.length > 0 ? (
                                        <div className="space-y-2">
                                            <div className="flex items-center gap-2 p-3 bg-error-500/10 border border-error-500/30 rounded-xl text-error-400 text-sm font-semibold">
                                                <AlertTriangle size={16} /> {importErrors.length} error{importErrors.length > 1 ? 'es' : ''} encontrado{importErrors.length > 1 ? 's' : ''} — corregí el archivo e intentá de nuevo
                                            </div>
                                            <div className="space-y-1 max-h-60 overflow-y-auto">
                                                {importErrors.map((e, i) => (
                                                    <div key={i} className="flex gap-3 text-xs p-2 rounded-lg bg-error-500/5 border border-error-500/10">
                                                        <span className="font-mono text-error-400 shrink-0">Fila {e.fila}</span>
                                                        <span className="text-slate-600 dark:text-slate-300">{e.error}</span>
                                                    </div>
                                                ))}
                                            </div>
                                            <button
                                                onClick={() => { setImportPhase('select'); setImportFile(null); setImportErrors([]); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                                                className="text-sm text-slate-500 dark:text-gray-400 hover:text-slate-700 dark:hover:text-white underline"
                                            >
                                                ← Seleccionar otro archivo
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            <div className="flex items-center justify-between">
                                                <p className="text-sm text-slate-600 dark:text-gray-300">
                                                    <span className="font-bold text-exito-400">{importPreview.length}</span> producto{importPreview.length !== 1 ? 's' : ''} listos para importar
                                                </p>
                                                <button
                                                    onClick={() => { setImportPhase('select'); setImportFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                                                    className="text-xs text-slate-500 dark:text-gray-400 hover:underline"
                                                >
                                                    ← Cambiar archivo
                                                </button>
                                            </div>
                                            <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-white/10 max-h-72">
                                                <table className="w-full text-xs">
                                                    <thead>
                                                        <tr className="bg-slate-50 dark:bg-white/5 text-slate-500 dark:text-gray-500 text-left">
                                                            <th className="px-3 py-2">Nombre</th>
                                                            <th className="px-3 py-2">Código</th>
                                                            <th className="px-3 py-2">Categoría</th>
                                                            <th className="px-3 py-2 text-right">Costo</th>
                                                            <th className="px-3 py-2 text-right">Venta</th>
                                                            <th className="px-3 py-2 text-center">IVA</th>
                                                            <th className="px-3 py-2 text-center">Stock</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="tabla-filas">
                                                        {importPreview.map((f, i) => (
                                                            <tr key={i} className="hover:bg-slate-50 dark:hover:bg-white/5">
                                                                <td className="px-3 py-2 font-medium text-slate-800 dark:text-white">{f.nombre}</td>
                                                                <td className="px-3 py-2 font-mono text-slate-500 dark:text-gray-400">{f.codigo || <span className="italic opacity-40">auto</span>}</td>
                                                                <td className="px-3 py-2 text-slate-500 dark:text-gray-400">{f.categoria || '—'}</td>
                                                                <td className="px-3 py-2 text-right font-mono text-slate-500 dark:text-gray-400">Gs. {fmt(f.precio_costo)}</td>
                                                                <td className="px-3 py-2 text-right font-mono font-bold text-slate-800 dark:text-white">Gs. {fmt(f.precio_venta)}</td>
                                                                <td className="px-3 py-2 text-center text-slate-500 dark:text-gray-400">
                                                                    {f.tasa_iva === 0 ? 'Exenta' : `${f.tasa_iva}%`}
                                                                </td>
                                                                <td className="px-3 py-2 text-center text-slate-600 dark:text-gray-300">{f.stock}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}

                            {/* Phase: done */}
                            {importPhase === 'done' && importResult && (
                                <div className="text-center py-6 space-y-4">
                                    <CheckCircle size={48} className="mx-auto text-exito-400" />
                                    <p className="titulo-modal">¡Importación exitosa!</p>
                                    <div className="flex justify-center gap-6">
                                        <div className="text-center">
                                            <p className="text-3xl font-black text-exito-400">{importResult.creados}</p>
                                            <p className="text-xs text-slate-500 dark:text-gray-400">Creados</p>
                                        </div>
                                        <div className="text-center">
                                            <p className="text-3xl font-black text-brand-800">{importResult.actualizados}</p>
                                            <p className="text-xs text-slate-500 dark:text-gray-400">Actualizados</p>
                                        </div>
                                        <div className="text-center">
                                            <p className="text-3xl font-black text-slate-700 dark:text-white">{importResult.total}</p>
                                            <p className="text-xs text-slate-500 dark:text-gray-400">Total</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="p-6 border-t border-slate-200 dark:border-white/10 flex justify-end gap-3">
                            <button onClick={() => setIsImportOpen(false)} className="px-5 py-2.5 rounded-xl text-slate-500 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-white/10 transition-colors">
                                {importPhase === 'done' ? 'Cerrar' : 'Cancelar'}
                            </button>
                            {importPhase === 'preview' && importErrors.length === 0 && (
                                <button
                                    onClick={handleImportConfirm}
                                    disabled={importLoading}
                                    className="bg-brand-500 hover:bg-brand-400 text-negro-950 px-6 py-2.5 rounded-xl font-semibold disabled:opacity-50 flex items-center gap-2 transition-all"
                                >
                                    {importLoading ? 'Importando...' : <><CheckCircle size={16} /> Confirmar importación</>}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
